import { useEffect, useState } from 'react';

/**
 * Demo-login prefill for the try.getdiscovr.ai gateway.
 *
 * The gateway links to `/login#demo_email=…&demo_password=…`. A URL fragment is
 * never sent to a server, so the credentials stay out of CloudFront/S3 logs and
 * Referer headers. Only allowlisted demo accounts are accepted — a crafted link
 * can't pre-fill an arbitrary account — and nothing is submitted: the visitor
 * still clicks "Log in".
 */
export interface DemoPrefill {
  email: string;
  password: string;
}

/** Parses a `#demo_email=…&demo_password=…` fragment; null unless the email is allowlisted. */
export function parseDemoPrefill(hash: string, allowedEmails: readonly string[]): DemoPrefill | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const email = params.get('demo_email')?.trim() ?? '';
  const password = params.get('demo_password') ?? '';
  if (!email || !password) return null;
  const allowed = allowedEmails.some((a) => a.toLowerCase() === email.toLowerCase());
  return allowed ? { email, password } : null;
}

/**
 * Reads the demo prefill from the current URL once, then strips the fragment so
 * the password doesn't linger in the address bar or get copied with the link.
 */
export function useDemoPrefill(allowedEmails: readonly string[]): DemoPrefill | null {
  const [prefill] = useState(() =>
    typeof window === 'undefined' ? null : parseDemoPrefill(window.location.hash, allowedEmails),
  );

  useEffect(() => {
    if (!prefill) return;
    const { pathname, search } = window.location;
    window.history.replaceState(window.history.state, '', pathname + search);
  }, [prefill]);

  return prefill;
}
